import React, { useMemo, useState, useRef, useCallback } from 'react';
import { Sun, Moon, ChevronUp, ChevronDown, Minus, Building2 as CityIcon } from 'lucide-react';
import { RoomMockup, coverTransformQuad } from '../fabric/RoomMockup';
import { ROOM_SCENES, sceneAmbiences, resolveScene } from '../fabric/roomScenes';
import { classifyFabric } from '../fabric/fabricLight';
import { resolveImageUrl } from '../../lib/imageCache';
import { usePreviewPrefs } from './usePreviewPrefs';
import { ROMAN_FOLDS, FOLD_INFO, useRomanFold, useRomanLining, linedLight } from '../../lib/romanFold';


const clamp01 = (v) => Math.max(0, Math.min(1, v));

/* Configurator fallback preview: real fabric texture on a room photo, proportioned to the configured size.
   Works with textureUrl (preferred) or hexColor (solid-color fallback).
   containerAspect forces the preview to match the standard Configurator 3:4 portrait viewport.
   Shoppers can drag the shade hem (mouse/touch) or use the slider/buttons to set how far it is lowered. */
/* Roman Vol. 1 / Vol. 2 (roman-shades collections) render as a folded roman shade, everything else as a roller */
export const isRomanFabric = (collection, color) => /\broman\b/i.test(`${collection?.name || ''} ${color?.collection_name || ''}`);


export const ConfiguratorShadePreview = ({ selectedColor, selectedCollection, selectedMaterial, config, toggle = null }) => {
  const [fold, setFold] = useRomanFold();
  const [lining] = useRomanLining();
  const [{ roomId, ambience, drop }, setPrefs] = usePreviewPrefs();
  const setRoomId = (v) => setPrefs({ roomId: v });
  const setAmbience = (v) => setPrefs({ ambience: v });
  const setDrop = (v) => setPrefs({ drop: v });
  const [dragging, setDragging] = useState(false);
  const stageRef = useRef(null);
  const room = ROOM_SCENES.find(s => s.id === roomId) || ROOM_SCENES[0];
  const ambiences = sceneAmbiences(room);
  const shownAmbience = ambiences.includes(ambience) ? ambience : ambiences[0];
  const base = useMemo(() => resolveScene(room, shownAmbience), [room, shownAmbience]);
  const width = Number(config?.width) || 0, height = Number(config?.height) || 0;

  /* Standard Configurator viewport: CSS aspect-ratio 3/4 → height/width = 4/3 */
  const CONFIGURATOR_ASPECT = 4 / 3;

  /* Showroom rooms: the shade always fills the real window opening exactly (the configured size drives the quote, not the mock) */
  const scene = base;

  /* Vertical extent (in % of the stage) covered by the fully lowered shade(s) — used for hem dragging */
  const windowBox = useMemo(() => {
    const ys = scene.shades.flatMap(s => coverTransformQuad(s.quad, scene.aspect, CONFIGURATOR_ASPECT).map(p => p[1]));
    const xs = scene.shades.flatMap(s => coverTransformQuad(s.quad, scene.aspect, CONFIGURATOR_ASPECT).map(p => p[0]));
    return { top: Math.min(...ys), bottom: Math.max(...ys), left: Math.min(...xs), right: Math.max(...xs) };
  }, [scene, CONFIGURATOR_ASPECT]);

  const dropFromPointer = useCallback((clientY) => {
    const el = stageRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    const yPct = ((clientY - r.top) / r.height) * 100;
    return clamp01((yPct - windowBox.top) / (windowBox.bottom - windowBox.top));
  }, [windowBox]);

  const onPointerDown = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragging(true);
    const d = dropFromPointer(e.clientY);
    if (d !== null) setDrop(d);
  };
  const onPointerMove = (e) => {
    if (!dragging) return;
    const d = dropFromPointer(e.clientY);
    if (d !== null) setDrop(d);
  };
  const endDrag = () => setDragging(false);

  const light = useMemo(() => classifyFabric(
    selectedColor,
    selectedMaterial,
    { collection_name: selectedCollection?.name, fabric_type: selectedCollection?.fabric_type || config?.fabric_material, openness: selectedCollection?.openness },
  ), [selectedColor, selectedMaterial, selectedCollection, config?.fabric_material]);

  /* Read the user's selected openness (e.g. "3%") from the configurator; fall back to 3 or first available */
  const openness = useMemo(() => {
    if (light.mode !== 'screen') return 3;
    const sel = parseInt(config?.selected_openness, 10);
    if (sel && light.options?.includes(sel)) return sel;
    return light.options?.includes(3) ? 3 : (light.options?.[0] || 3);
  }, [light, config?.selected_openness]);
  const textureUrl = resolveImageUrl(selectedColor?.image_url, 512);
  const hexColor = selectedColor?.hex_color || selectedColor?.hex || null;
  const pct = Math.round(drop * 100);

  const roman = isRomanFabric(selectedCollection, selectedColor);
  const shown = roman ? linedLight(light, light.opacity || 0.85, lining) : { light, filterOpacity: light.opacity || 0.85 };
  return (
    <>
    <div className="relative" data-testid="configurator-shade-preview" data-drop={drop.toFixed(2)}>
      <div ref={stageRef}>
        <RoomMockup scene={scene} ambience={shownAmbience} textureUrl={textureUrl} hexColor={hexColor} light={shown.light} filterOpacity={shown.filterOpacity} openness={openness} drop={drop} animate={!dragging} containerAspect={CONFIGURATOR_ASPECT} system={roman ? 'roman' : 'roller'} fold={fold}>
          {/* Drag surface over the window: drag the hem up/down */}
          <div
            className={`absolute z-20 ${dragging ? 'cursor-grabbing' : 'cursor-ns-resize'}`}
            style={{ top: `${windowBox.top}%`, bottom: `${100 - windowBox.bottom}%`, left: `${windowBox.left}%`, right: `${100 - windowBox.right}%`, touchAction: 'none' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            title="Drag to raise or lower the shade"
            data-testid="cfg-shade-drag-area"
          />
        </RoomMockup>
      </div>
      {ambiences.length > 1 && <div className="absolute top-3 right-3 flex rounded-lg bg-white/90 shadow p-0.5 z-30" data-testid="cfg-ambience">
        {[['day', Sun, 'Day'], ['night', Moon, 'Night'], ['nightCity', CityIcon, 'Night City']].filter(([k]) => ambiences.includes(k)).map(([k, Icon, label]) => (
          <button key={k} type="button" onClick={() => setAmbience(k)} title={label} className={`w-7 h-7 rounded-md flex items-center justify-center ${shownAmbience === k ? 'bg-neutral-900 text-white' : 'text-neutral-600'}`} data-testid={`cfg-ambience-${k}`}>
            <Icon className="w-3.5 h-3.5" />
          </button>
        ))}
      </div>}

      {/* Shade drop controls: Up / Half / Down + vertical slider */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1 rounded-xl bg-white/90 shadow p-1 z-30" data-testid="cfg-drop-controls">
        <button type="button" onClick={() => setDrop(0)} title="Raise shade" className="w-7 h-7 rounded-md flex items-center justify-center text-neutral-700 hover:bg-neutral-100" data-testid="cfg-drop-up">
          <ChevronUp className="w-4 h-4" />
        </button>
        <input
          type="range" min="0" max="100" step="1" value={pct}
          onChange={e => setDrop(parseInt(e.target.value, 10) / 100)}
          aria-label="Shade position"
          className="accent-neutral-900 cursor-pointer"
          style={{ writingMode: 'vertical-lr', direction: 'ltr', height: 96, width: 18 }}
          data-testid="cfg-drop-slider"
        />
        <button type="button" onClick={() => setDrop(0.5)} title="Half lowered" className="w-7 h-7 rounded-md flex items-center justify-center text-neutral-700 hover:bg-neutral-100" data-testid="cfg-drop-half">
          <Minus className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => setDrop(1)} title="Lower shade" className="w-7 h-7 rounded-md flex items-center justify-center text-neutral-700 hover:bg-neutral-100" data-testid="cfg-drop-down">
          <ChevronDown className="w-4 h-4" />
        </button>
        <span className="text-[10px] font-medium text-neutral-700 tabular-nums leading-none pb-0.5" data-testid="cfg-drop-value">{pct}%</span>
      </div>

      {width > 0 && height > 0 && (
        <div className="absolute bottom-3 left-3 bg-neutral-900/70 text-white text-[11px] px-2.5 py-1 rounded-md z-30" data-testid="cfg-size-label">
          {width}" × {height}" · {shown.light.label}{shown.light.mode === 'screen' ? ` · ${openness}% open` : ''} · {pct}% lowered
        </div>
      )}
      {!width && (
        <div className="absolute bottom-3 left-3 bg-neutral-900/70 text-white text-[11px] px-2.5 py-1 rounded-md z-30" data-testid="cfg-openness-label">
          {shown.light.label}{shown.light.mode === 'screen' ? ` · ${openness}% openness` : ''} · {pct}% lowered
        </div>
      )}
      {room.exterior && (
        <div className="absolute top-12 right-3 bg-neutral-900/70 text-amber-200 text-[10px] px-2 py-0.5 rounded-md z-30" data-testid="cfg-street-label">
          Lights on · seen from the street
        </div>
      )}
      {roman && (
        <div className={`absolute ${room.exterior ? "top-20" : "top-12"} right-3 flex flex-col rounded-lg bg-white/90 shadow p-0.5 z-30`} data-testid="cfg-fold-style">
          {ROMAN_FOLDS.map(([id, label]) => (
            <button key={id} type="button" onClick={() => setFold(id)} title={FOLD_INFO[id]}
              className={`px-2 py-1 rounded-md text-[10px] font-medium text-left transition-colors ${fold === id ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:bg-neutral-100'}`}
              data-testid={`cfg-fold-${id}`}>{label}</button>
          ))}
        </div>
      )}
      {toggle}
    </div>
    {/* Room picker with names */}
    <div className="mt-2 flex sm:grid sm:grid-cols-8 gap-1 overflow-x-auto pb-1 [mask-image:linear-gradient(to_right,black_85%,transparent)] sm:[mask-image:none]" data-testid="cfg-room-picker">
      {ROOM_SCENES.map(r => {
        const Icon = r.icon;
        const active = roomId === r.id;
        return (
          <button key={r.id} type="button" onClick={() => setRoomId(r.id)} title={r.name}
            className={`flex-shrink-0 flex flex-col items-center gap-0.5 w-[58px] sm:w-auto min-w-0 rounded-lg border px-0.5 py-1.5 transition-colors ${active ? 'bg-neutral-900 border-neutral-900 text-white' : 'bg-white border-neutral-200 text-neutral-600 hover:border-neutral-400'}`}
            data-testid={`cfg-room-${r.id}`}>
            <Icon className="w-4 h-4" />
            <span className="text-[10px] leading-tight text-center whitespace-nowrap truncate max-w-full">{r.name.replace(' Room', '').replace('Home ', '')}</span>
          </button>
        );
      })}
    </div>
    </>
  );
};

export default ConfiguratorShadePreview;
